import type { Locale } from './i18n.ts';

/**
 * Presentation helpers. All money arrives from the API as integer minor units;
 * this is the only place it becomes a decimal string.
 */

const EXPONENTS: Record<string, number> = { KWD: 3, BHD: 3, OMR: 3, JPY: 0 };
const SYMBOLS: Record<string, { en: string; ar: string }> = {
  AED: { en: 'AED', ar: 'د.إ' },
  SAR: { en: 'SAR', ar: 'ر.س' },
  USD: { en: '$', ar: '$' },
  EUR: { en: '€', ar: '€' },
  GBP: { en: '£', ar: '£' },
  INR: { en: '₹', ar: '₹' },
};

function exponentFor(currency: string): number {
  return EXPONENTS[currency] ?? 2;
}

export function formatMoney(
  minor: number | null | undefined,
  currency: string,
  opts: { locale?: Locale; compact?: boolean; bare?: boolean; signed?: boolean } = {},
): string {
  if (minor == null) return '—';
  const { locale = 'en', compact = false, bare = false, signed = false } = opts;
  const exp = exponentFor(currency);
  const value = minor / 10 ** exp;

  // Whole-unit rounding above a threshold keeps dashboards from turning into
  // walls of digits, while small amounts keep their fils.
  const useCompact = compact && Math.abs(value) >= 1000;
  const digits = useCompact ? 0 : exp;

  const nf = new Intl.NumberFormat(locale === 'ar' ? 'ar-AE' : 'en-AE', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    // Latin digits in Arabic too: financial UIs in the Gulf overwhelmingly use
    // them, and mixed numerals in one screen read badly.
    numberingSystem: 'latn',
  });

  const sign = signed && minor > 0 ? '+' : '';
  const num = sign + nf.format(value);
  if (bare) return num;

  const symbol = SYMBOLS[currency]?.[locale] ?? currency;
  return locale === 'ar' ? `${num} ${symbol}` : `${symbol} ${num}`;
}

/**
 * An abbreviated amount for a chart axis tick — "12.5K" rather than "12,500".
 *
 * Spelled out, a tick is wider than the gutter beside a chart on a phone, and
 * widening the gutter squeezes the plot into a strip. A gridline is a scale
 * rather than a figure to be read off exactly; the exact number is printed
 * elsewhere on the card.
 */
export function formatAxisTick(minor: number, currency: string, locale: Locale = 'en'): string {
  const value = minor / 10 ** exponentFor(currency);
  if (value === 0) return '0';
  return new Intl.NumberFormat(locale === 'ar' ? 'ar-AE' : 'en-AE', {
    notation: 'compact',
    maximumFractionDigits: 1,
    numberingSystem: 'latn',
  }).format(value);
}

export function formatPct(value: number | null | undefined, digits = 0): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value >= 0 ? '' : ''}${value.toFixed(digits)}%`;
}

export function formatDate(iso: string, locale: Locale, opts: { withTime?: boolean; relative?: boolean } = {}): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';

  if (opts.relative) {
    const now = new Date();
    const days = Math.floor((startOfDay(now) - startOfDay(d)) / 86_400_000);
    if (days === 0) return locale === 'ar' ? 'اليوم' : 'Today';
    if (days === 1) return locale === 'ar' ? 'أمس' : 'Yesterday';
    if (days > 1 && days < 7) {
      return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-AE' : 'en-AE', { weekday: 'long' }).format(d);
    }
  }

  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-AE' : 'en-AE', {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
    ...(opts.withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
    numberingSystem: 'latn',
  }).format(d);
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Groups transactions by calendar day for the sectioned activity list. */
export function groupByDay<T extends { occurredAt: string }>(items: T[]): { key: string; items: T[] }[] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = item.occurredAt.slice(0, 10);
    const list = groups.get(key) ?? [];
    list.push(item);
    groups.set(key, list);
  }
  return [...groups.entries()].map(([key, items]) => ({ key, items }));
}

/** Parses a user-typed amount into minor units, tolerating separators. */
export function parseAmountInput(value: string, currency: string): number | null {
  const cleaned = value.replace(/[^\d.,-]/g, '').replace(/,/g, '');
  if (!cleaned) return null;
  const num = Number(cleaned);
  if (!Number.isFinite(num) || num <= 0) return null;
  return Math.round(num * 10 ** exponentFor(currency));
}

export function minorToInput(minor: number | null | undefined, currency: string): string {
  if (minor == null) return '';
  return (minor / 10 ** exponentFor(currency)).toFixed(exponentFor(currency));
}

/** Sign convention: outflows are shown negative, inflows positive. */
export function signedAmount(minor: number, type: string): number {
  return type === 'income' || type === 'refund' ? minor : -minor;
}
